import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  createWebhook,
  deleteWebhook,
  getWebhooks,
  updateWebhook,
  UpsertWebhookDto,
  WebhookDto,
} from "../../../api/apiClient";
import config from "../../../config";
import PageHeader from "../../../app/layout/PageHeader";
import FrontlineDataGrid from "../../../components/common/FrontlineDataGrid";
import { notify } from "../../components-ui/toast";

import "../../../css/buttons.css";
import "../../../css/cards.css";
import "../../../css/forms.css";
import "../../../css/tables.css";

const SUPPORTED_EVENTS = ["SMS_STATUS", "SMS_INBOUND", "OPT_OUT"];
const DEFAULT_WEBHOOK_URL = `${config.API_BASE_URL.replace(/\/$/, "")}/api/sms/webhook`;

type WebhookRow = WebhookDto & {
  eventSummary: string;
};

const emptyForm: UpsertWebhookDto = {
  name: "FrontLine SMS Webhook",
  url: DEFAULT_WEBHOOK_URL,
  rateLimit: 10,
  events: [...SUPPORTED_EVENTS],
  sender: [],
};

function parseSenderNumbers(value: string) {
  return value
    .split(/[\n,]/)
    .map((sender) => sender.trim())
    .filter(Boolean);
}

export default function WebhookManagementPage() {
  const [webhooks, setWebhooks] = useState<WebhookDto[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<WebhookDto | null>(null);
  const [form, setForm] = useState<UpsertWebhookDto>(emptyForm);
  const [senderInput, setSenderInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebhookDto | null>(null);

  async function refresh() {
    const result = await getWebhooks();
    setWebhooks(result);
  }

  useEffect(() => {
    refresh();
  }, []);

  const rows = useMemo<WebhookRow[]>(
    () =>
      webhooks.map((webhook) => ({
        ...webhook,
        eventSummary: webhook.events.join(", "),
      })),
    [webhooks],
  );

  const columns: GridColDef<WebhookRow>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 0.9,
      minWidth: 180,
    },
    {
      field: "url",
      headerName: "URL",
      flex: 1.4,
      minWidth: 260,
    },
    {
      field: "eventSummary",
      headerName: "Events",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "actions",
      headerName: "",
      width: 104,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <div className="frontline-grid-actions">
          <Tooltip title="Edit webhook">
            <IconButton
              size="small"
              className="frontline-grid-action-button"
              onClick={() => openEditDialog(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete webhook">
            <IconButton
              size="small"
              className="frontline-grid-action-button"
              sx={{ color: "crimson" }}
              onClick={() => setDeleteTarget(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  function openCreateDialog() {
    setEditingWebhook(null);
    setForm(emptyForm);
    setSenderInput("");
    setDialogOpen(true);
  }

  function openEditDialog(webhook: WebhookDto) {
    setEditingWebhook(webhook);
    setForm({
      name: webhook.name,
      url: webhook.url,
      rateLimit: webhook.rateLimit || 10,
      events: webhook.events,
      sender: webhook.sender || [],
    });
    setSenderInput((webhook.sender || []).join("\n"));
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingWebhook(null);
    setForm(emptyForm);
    setSenderInput("");
  }

  function toggleEvent(eventType: string) {
    setForm((current) => ({
      ...current,
      events: current.events.includes(eventType)
        ? current.events.filter((event) => event !== eventType)
        : [...current.events, eventType],
    }));
  }

  function validateForm() {
    if (!form.name.trim()) {
      notify.error("Webhook name is required.");
      return false;
    }

    if (!/^https:\/\/.+/i.test(form.url.trim())) {
      notify.error("Webhook URL must be HTTPS.");
      return false;
    }

    if (form.events.length === 0) {
      notify.error("Select at least one event.");
      return false;
    }

    if (!Number.isInteger(form.rateLimit) || form.rateLimit <= 0) {
      notify.error("Rate limit must be greater than zero.");
      return false;
    }

    if (parseSenderNumbers(senderInput).length === 0) {
      notify.error("Enter at least one sender number.");
      return false;
    }

    return true;
  }

  async function saveWebhook() {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        sender: parseSenderNumbers(senderInput),
      };

      if (editingWebhook) {
        await updateWebhook(editingWebhook.id, payload);
      } else {
        await createWebhook(payload);
      }

      closeDialog();
      await refresh();
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;

    await deleteWebhook(deleteTarget.id);
    setDeleteTarget(null);
    await refresh();
  }

  return (
    <div className="page-content">
      <PageHeader
        title="Webhooks"
        subtitle="Manage Kudosity SMS webhook registrations."
      />

      <section className="card">
        <div className="card-header-row">
          <div>
            <h2 className="card-title">Kudosity Webhooks</h2>
          </div>
          <div className="card-header-actions">
            <Tooltip title="Refresh webhooks">
              <IconButton size="small" onClick={refresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Create Webhook
            </Button>
          </div>
        </div>

        <FrontlineDataGrid
          className="frontline-grid"
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
        />
      </section>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingWebhook ? "Edit Webhook" : "Create Webhook"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={form.name}
            onChange={(event) =>
              setForm((current) => ({ ...current, name: event.target.value }))
            }
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="URL"
            value={form.url}
            onChange={(event) =>
              setForm((current) => ({ ...current, url: event.target.value }))
            }
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Rate limit"
            type="number"
            value={form.rateLimit}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                rateLimit: Number(event.target.value),
              }))
            }
            fullWidth
            margin="dense"
            required
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Sender numbers"
            value={senderInput}
            onChange={(event) => setSenderInput(event.target.value)}
            helperText="Enter one sender number per line, or separate numbers with commas."
            fullWidth
            margin="dense"
            required
            multiline
            minRows={2}
          />
          <div className="card-form-section-title">Events</div>
          <div>
            {SUPPORTED_EVENTS.map((eventType) => (
              <FormControlLabel
                key={eventType}
                control={
                  <Checkbox
                    checked={form.events.includes(eventType)}
                    onChange={() => toggleEvent(eventType)}
                  />
                }
                label={eventType}
              />
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveWebhook} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Webhook</DialogTitle>
        <DialogContent>
          Delete {deleteTarget?.name || "this webhook"}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
